import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiUrl } from "../../config/api";
import AnimatedTabPanel from "./AnimatedTabPanel";
import { MARKETING_URL_TEMPLATES } from "./tableeMarketingTemplates";
import "./tableeCarousel.css";

const emptyConfig = {
  businessName: "Tablee",
  legalName: "",
  contactEmail: "contacto@tablee.cl",
  salesEmail: "",
  supportEmail: "",
  financeEmail: "",
  phone: "+56 9 0000 0000",
  whatsapp: "",
  addressLine1: "",
  addressLine2: "",
  commune: "",
  city: "Santiago",
  region: "Región Metropolitana",
  country: "Chile",
  instagramUrl: "",
  facebookUrl: "",
  linkedinUrl: "",
  footerTagline: "Sabores que reunen. Insumos seleccionados para comprar facil, cocinar mejor y resolver cada mesa.",
  helpText: "Despachos y retiros coordinados",
  deliveryNoticeEnabled: true,
  deliveryNoticeText: "Despachamos en la Región Metropolitana",
  deliveryNoticeLinkLabel: "Ver cobertura y condiciones",
  deliveryNoticeTitle: "Despachos y retiros",
  deliveryNoticeBody: "Elige despacho a domicilio o retiro coordinado. La cobertura, los plazos y el valor dependen de la opción seleccionada.",
  aboutEyebrow: "Quienes somos",
  aboutTitle: "Una despensa pensada para reunir",
  aboutBody: "Tablee nace para simplificar la compra de insumos ricos, practicos y confiables para cocinas, reuniones, oficinas y celebraciones. Seleccionamos productos que funcionan bien juntos, con foco en sabor, abastecimiento simple y una experiencia de compra clara.",
  aboutImageUrl: "/tablee_hero.jpg",
  aboutImageZoom: 1,
  aboutImagePositionX: 50,
  aboutImagePositionY: 50,
  aboutTags: ["Food supply", "Compras simples", "Sabores para compartir"],
  aboutFact1: "Food supply",
  aboutFact2: "Compras simples",
  aboutFact3: "Sabores para compartir",
};

const sections = [
  {
    id: "company",
    title: "Empresa",
    icon: "storefront",
    fields: [
      ["businessName", "Nombre comercial"],
      ["legalName", "Razón social"],
    ],
  },
  {
    id: "contact",
    title: "Contacto",
    icon: "contact_mail",
    fields: [
      ["contactEmail", "Correo principal", "email"],
      ["salesEmail", "Correo ventas", "email"],
      ["supportEmail", "Correo soporte", "email"],
      ["financeEmail", "Correo finanzas", "email"],
      ["phone", "Teléfono"],
      ["whatsapp", "WhatsApp"],
    ],
  },
  {
    id: "address",
    title: "Dirección",
    icon: "location_on",
    fields: [
      ["addressLine1", "Dirección línea 1"],
      ["addressLine2", "Dirección línea 2"],
      ["commune", "Comuna"],
      ["city", "Ciudad"],
      ["region", "Región"],
      ["country", "País"],
    ],
  },
  {
    id: "social",
    title: "Redes",
    icon: "share",
    fields: [
      ["instagramUrl", "Instagram"],
      ["facebookUrl", "Facebook"],
      ["linkedinUrl", "LinkedIn"],
    ],
  },
  {
    id: "footer",
    title: "Footer",
    icon: "subject",
    fields: [
      ["footerTagline", "Descripción footer", "textarea"],
      ["helpText", "Texto de ayuda"],
    ],
  },
  {
    id: "delivery-notice",
    title: "Aviso de despacho",
    icon: "local_shipping",
    fields: [
      ["deliveryNoticeText", "Mensaje corto"],
      ["deliveryNoticeLinkLabel", "Texto del enlace"],
      ["deliveryNoticeTitle", "Título del detalle"],
      ["deliveryNoticeBody", "Descripción", "textarea"],
    ],
  },
  {
    id: "about",
    title: "Quienes somos",
    icon: "groups",
    fields: [
      ["aboutEyebrow", "Etiqueta"],
      ["aboutTitle", "Título"],
      ["aboutBody", "Texto principal", "textarea"],
    ],
  },
];

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function getFieldValue(config, field) {
  return config[field] || "";
}

function buildAddress(config) {
  return [
    config.addressLine1,
    config.addressLine2,
    config.commune,
    config.city,
    config.region,
    config.country,
  ].filter(Boolean).join(", ");
}

const aboutContentFields = sections.find((section) => section.id === "about").fields;
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("No se pudo leer la imagen."));
    reader.readAsDataURL(file);
  });
}

function normalizeAboutImageZoom(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Number(Math.min(2.5, Math.max(0.5, parsed)).toFixed(2));
}

function normalizeAboutImagePosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, Math.round(parsed)));
}

function normalizeAboutTags(config) {
  const source = Array.isArray(config?.aboutTags)
    ? config.aboutTags
    : [config?.aboutFact1, config?.aboutFact2, config?.aboutFact3];
  return source.map((tag) => String(tag || "").trim()).filter(Boolean).slice(0, 12);
}

function TableeGeneralConfig({ apiPrefix = "/tablee", storeName = "Tablee", storeLogo = "/images/tablee_logo.png", storeUrl = "www.tablee.cl" } = {}) {
  const defaultConfig = useMemo(() => ({ ...emptyConfig, ...(apiPrefix !== "/tablee" ? {
    businessName: storeName, contactEmail: "", footerTagline: "Momentos para compartir.",
    aboutTitle: "Celebraciones con Oliva y Miel", aboutBody: "", aboutImageUrl: storeLogo,
    aboutTags: [], aboutFact1: "", aboutFact2: "", aboutFact3: "",
  } : {}) }), [apiPrefix, storeName, storeLogo]);
  const marketingTemplates = MARKETING_URL_TEMPLATES.map((template) => ({ ...template, value: template.value.replaceAll("https://www.tablee.cl/", `https://${storeUrl}/`) }));
  const aboutImageInputRef = useRef(null);
  const aboutImageDragRef = useRef(null);
  const [config, setConfig] = useState(defaultConfig);
  const [draft, setDraft] = useState(defaultConfig);
  const [activeSectionId, setActiveSectionId] = useState("");
  const [aboutModalTab, setAboutModalTab] = useState("content");
  const [newAboutTag, setNewAboutTag] = useState("");
  const [editingAboutTagIndex, setEditingAboutTagIndex] = useState(-1);
  const [isUploadingAboutImage, setIsUploadingAboutImage] = useState(false);
  const [isDraggingAboutImage, setIsDraggingAboutImage] = useState(false);
  const [isMarketingModalOpen, setIsMarketingModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [copiedMarketingTemplate, setCopiedMarketingTemplate] = useState("");
  const [loading, setLoading] = useState(true);

  const activeSection = useMemo(
    () => sections.find((section) => section.id === activeSectionId),
    [activeSectionId]
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/general-config`), {
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo cargar la configuración.");
        return;
      }
      const nextConfig = { ...defaultConfig, ...(data.config || {}) };
      nextConfig.aboutTags = normalizeAboutTags(nextConfig);
      setConfig(nextConfig);
      setDraft(nextConfig);
    } catch (err) {
      setMessage("No se pudo cargar la configuración.");
    } finally {
      setLoading(false);
    }
  }, [apiPrefix, defaultConfig]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (!activeSectionId && !isMarketingModalOpen) return undefined;
    function handleEscape(event) {
      if (event.key === "Escape") {
        if (isMarketingModalOpen) {
          setIsMarketingModalOpen(false);
        } else {
          setActiveSectionId("");
          setDraft(config);
        }
      }
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [activeSectionId, config, isMarketingModalOpen]);

  function openModal(sectionId) {
    setDraft(config);
    setActiveSectionId(sectionId);
    setAboutModalTab("content");
    setNewAboutTag("");
    setEditingAboutTagIndex(-1);
    setMessage("");
  }

  function closeModal() {
    setActiveSectionId("");
    setDraft(config);
    setNewAboutTag("");
    setEditingAboutTagIndex(-1);
  }

  function updateDraft(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  async function copyMarketingTemplate(template) {
    try {
      await navigator.clipboard.writeText(template.value);
      setCopiedMarketingTemplate(template.id);
      window.setTimeout(() => {
        setCopiedMarketingTemplate((current) => (current === template.id ? "" : current));
      }, 1800);
    } catch (err) {
      setMessage("No se pudo copiar. Selecciona el texto manualmente.");
    }
  }

  function startAboutImageDrag(event) {
    if (event.button != null && event.button !== 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    aboutImageDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      width: bounds.width || 1,
      height: bounds.height || 1,
      positionX: normalizeAboutImagePosition(draft.aboutImagePositionX),
      positionY: normalizeAboutImagePosition(draft.aboutImagePositionY),
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsDraggingAboutImage(true);
  }

  function dragAboutImage(event) {
    const drag = aboutImageDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const deltaX = ((event.clientX - drag.startX) / drag.width) * 100;
    const deltaY = ((event.clientY - drag.startY) / drag.height) * 100;
    setDraft((current) => ({
      ...current,
      aboutImagePositionX: normalizeAboutImagePosition(drag.positionX + deltaX),
      aboutImagePositionY: normalizeAboutImagePosition(drag.positionY + deltaY),
    }));
  }

  function stopAboutImageDrag(event) {
    if (aboutImageDragRef.current?.pointerId !== event.pointerId) return;
    aboutImageDragRef.current = null;
    setIsDraggingAboutImage(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }

  function updateAboutTags(updater) {
    setDraft((current) => {
      const currentTags = normalizeAboutTags(current);
      const nextTags = typeof updater === "function" ? updater(currentTags) : updater;
      const aboutTags = normalizeAboutTags({ aboutTags: nextTags });
      return {
        ...current,
        aboutTags,
        aboutFact1: aboutTags[0] || "",
        aboutFact2: aboutTags[1] || "",
        aboutFact3: aboutTags[2] || "",
      };
    });
  }

  function addAboutTag() {
    const tag = newAboutTag.trim();
    if (!tag) return;
    updateAboutTags((tags) => [...tags, tag]);
    setNewAboutTag("");
    setEditingAboutTagIndex(-1);
  }

  function updateAboutTag(index, value) {
    updateAboutTags((tags) => tags.map((tag, tagIndex) => (tagIndex === index ? value : tag)));
  }

  function deleteAboutTag(index) {
    updateAboutTags((tags) => tags.filter((_, tagIndex) => tagIndex !== index));
    setEditingAboutTagIndex((current) => (current === index ? -1 : current > index ? current - 1 : current));
  }

  async function handleAboutImageFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setMessage("");
    setIsUploadingAboutImage(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const response = await fetch(apiUrl(`${apiPrefix}/product-images`), {
        method: "POST",
        headers: getSessionHeaders(),
        body: JSON.stringify({
          dataUrl,
          mimeType: file.type,
        }),
      });
      const data = await response.json();
      if (!data.success || !data.imageUrl) {
        setMessage(data.message || "No se pudo subir la imagen.");
        return;
      }
      updateDraft("aboutImageUrl", data.imageUrl);
    } catch (err) {
      setMessage("No se pudo subir la imagen.");
    } finally {
      setIsUploadingAboutImage(false);
    }
  }

  async function saveConfig(event) {
    event.preventDefault();
    setMessage("");
    const payload = {
      ...draft,
      aboutImageZoom: normalizeAboutImageZoom(draft.aboutImageZoom),
      aboutImagePositionX: normalizeAboutImagePosition(draft.aboutImagePositionX),
      aboutImagePositionY: normalizeAboutImagePosition(draft.aboutImagePositionY),
      aboutTags: normalizeAboutTags(draft),
    };
    payload.aboutFact1 = payload.aboutTags[0] || "";
    payload.aboutFact2 = payload.aboutTags[1] || "";
    payload.aboutFact3 = payload.aboutTags[2] || "";

    try {
      const response = await fetch(apiUrl(`${apiPrefix}/general-config`), {
        method: "PUT",
        headers: getSessionHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo guardar la configuración.");
        return;
      }
      const nextConfig = { ...defaultConfig, ...(data.config || {}) };
      nextConfig.aboutTags = normalizeAboutTags(nextConfig);
      setConfig(nextConfig);
      setDraft(nextConfig);
      setActiveSectionId("");
      setMessage("Configuración actualizada.");
    } catch (err) {
      setMessage("No se pudo guardar la configuración.");
    }
  }

  return (
    <div className="tablee-carousel tablee-general-config">
      <header className="tablee-carousel__header">
        <img src={storeLogo} alt={storeName} />
        <div>
          <span>{storeName}</span>
          <h1>Configuración General</h1>
          <p>Administra la información de empresa, contacto, dirección y textos comunes usados por {storeUrl}.</p>
        </div>
      </header>

      <section className="tablee-general-config__summary">
        <div>
          <span>Correo principal</span>
          <strong>{config.contactEmail}</strong>
        </div>
        <div>
          <span>Teléfono</span>
          <strong>{config.phone}</strong>
        </div>
        <div>
          <span>Dirección</span>
          <strong>{buildAddress(config) || "Sin dirección configurada"}</strong>
        </div>
        <div>
          <span>Quienes somos</span>
          <strong>{config.aboutTitle || "Sin texto configurado"}</strong>
        </div>
      </section>

      <section className="tablee-general-config__grid" aria-label="Secciones de configuración">
        {sections.map((section) => (
          <button type="button" key={section.id} onClick={() => openModal(section.id)}>
            <span className="material-symbols-outlined">{section.icon}</span>
            <strong>{section.title}</strong>
            <small>
              {section.id === "delivery-notice"
                ? (config.deliveryNoticeEnabled ? "Visible en el sitio" : "Oculto")
                : section.fields.map(([field]) => getFieldValue(config, field)).filter(Boolean)[0] || "Pendiente"}
            </small>
          </button>
        ))}
        <button type="button" onClick={() => setIsMarketingModalOpen(true)}>
          <span className="material-symbols-outlined">monitoring</span>
          <strong>Marketing</strong>
          <small>URLs para campañas</small>
        </button>
      </section>

      {loading ? <p className="tablee-carousel__empty">Cargando configuración...</p> : null}
      {message ? <p className="tablee-home-admin__message">{message}</p> : null}

      {isMarketingModalOpen ? (
        <div className="tablee-carousel__modal-backdrop" role="presentation" onMouseDown={() => setIsMarketingModalOpen(false)}>
          <div className="tablee-carousel__modal tablee-general-config__marketing-modal" role="dialog" aria-modal="true" aria-labelledby="tablee-marketing-links-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="tablee-carousel__modal-head">
              <div>
                <span>Marketing</span>
                <h2 id="tablee-marketing-links-title">URLs para campañas</h2>
              </div>
              <button type="button" className="tablee-carousel__icon-button" onClick={() => setIsMarketingModalOpen(false)} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="tablee-general-config__marketing-list tablee-general-config__marketing-modal-body">
              {marketingTemplates.map((template) => (
                <article className="tablee-general-config__marketing-template" key={template.id}>
                  <div>
                    <span className="material-symbols-outlined">{template.icon}</span>
                    <div>
                      <strong>{template.title}</strong>
                      <small>{template.helper}</small>
                    </div>
                  </div>
                  <code>{template.value}</code>
                  <button type="button" onClick={() => copyMarketingTemplate(template)}>
                    <span className="material-symbols-outlined">{copiedMarketingTemplate === template.id ? "check" : "content_copy"}</span>
                    {copiedMarketingTemplate === template.id ? "Copiado" : "Copiar"}
                  </button>
                </article>
              ))}
            </div>
            <div className="tablee-carousel__modal-actions">
              <span />
              <div className="tablee-carousel__footer-buttons">
                <button type="button" className="tablee-carousel__secondary" onClick={() => setIsMarketingModalOpen(false)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {activeSection ? (
        <div className="tablee-carousel__modal-backdrop" role="presentation" onMouseDown={closeModal}>
          <form
            className={`tablee-carousel__modal ${activeSection.id === "about" && aboutModalTab === "image" ? "tablee-general-config__modal--about-image" : ""}`.trim()}
            onSubmit={saveConfig}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="tablee-carousel__modal-head">
              <div>
                <span>Configuración General</span>
                <h2>{activeSection.title}</h2>
              </div>
              <button type="button" className="tablee-carousel__icon-button" onClick={closeModal} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            {activeSection.id === "about" ? (
              <>
                <div className="tablee-home-admin__section-tabs tablee-general-config__about-tabs" role="tablist" aria-label="Secciones de Quienes somos">
                  <button
                    type="button"
                    className={aboutModalTab === "content" ? "is-active" : ""}
                    onClick={() => setAboutModalTab("content")}
                    role="tab"
                    aria-selected={aboutModalTab === "content"}
                  >
                    Contenido
                  </button>
                  <button
                    type="button"
                    className={aboutModalTab === "image" ? "is-active" : ""}
                    onClick={() => setAboutModalTab("image")}
                    role="tab"
                    aria-selected={aboutModalTab === "image"}
                  >
                    Imagen
                  </button>
                  <button
                    type="button"
                    className={aboutModalTab === "tags" ? "is-active" : ""}
                    onClick={() => setAboutModalTab("tags")}
                    role="tab"
                    aria-selected={aboutModalTab === "tags"}
                  >
                    Tags
                  </button>
                </div>
                <AnimatedTabPanel tabKey={aboutModalTab} contentClassName="tablee-general-config__about-panel">
                  {aboutModalTab === "content" ? (
                    <div className="tablee-carousel__form-grid tablee-general-config__about-content" role="tabpanel">
                      {aboutContentFields.map(([field, label, type]) => (
                        <label className={type === "textarea" ? "tablee-carousel__field tablee-carousel__wide-field" : "tablee-carousel__field"} key={field}>
                          <span>{label}</span>
                          {type === "textarea" ? (
                            <textarea value={draft[field] || ""} onChange={(event) => updateDraft(field, event.target.value)} />
                          ) : (
                            <input type={type || "text"} value={draft[field] || ""} onChange={(event) => updateDraft(field, event.target.value)} />
                          )}
                        </label>
                      ))}
                    </div>
                  ) : null}
                  {aboutModalTab === "image" ? (
                    <div className="tablee-general-config__about-image" role="tabpanel">
                      <div
                        className={`tablee-general-config__about-image-preview ${isDraggingAboutImage ? "is-dragging" : ""}`}
                        onPointerDown={startAboutImageDrag}
                        onPointerMove={dragAboutImage}
                        onPointerUp={stopAboutImageDrag}
                        onPointerCancel={stopAboutImageDrag}
                      >
                        <img
                          src={draft.aboutImageUrl || storeLogo}
                          alt=""
                          style={{
                            objectPosition: `${normalizeAboutImagePosition(draft.aboutImagePositionX)}% ${normalizeAboutImagePosition(draft.aboutImagePositionY)}%`,
                            transform: `scale(${normalizeAboutImageZoom(draft.aboutImageZoom)})`,
                          }}
                        />
                      </div>
                      <div className="tablee-general-config__about-image-controls">
                        <label className="tablee-carousel__field tablee-home-admin__range-field">
                          <span>Zoom {normalizeAboutImageZoom(draft.aboutImageZoom).toFixed(2)}x</span>
                          <input
                            type="range"
                            min="0.5"
                            max="2.5"
                            step="0.05"
                            value={normalizeAboutImageZoom(draft.aboutImageZoom)}
                            onChange={(event) => updateDraft("aboutImageZoom", Number(event.target.value))}
                          />
                        </label>
                      </div>
                    </div>
                  ) : null}
                  {aboutModalTab === "tags" ? (
                    <div className="tablee-general-config__tags" role="tabpanel">
                      <div className="tablee-general-config__tags-add">
                        <label className="tablee-carousel__field">
                          <span>Nuevo tag</span>
                          <input
                            type="text"
                            value={newAboutTag}
                            onChange={(event) => setNewAboutTag(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                addAboutTag();
                              }
                            }}
                          />
                        </label>
                        <button type="button" onClick={addAboutTag} disabled={!newAboutTag.trim()}>
                          Agregar
                        </button>
                      </div>

                      <div className="tablee-general-config__tags-table">
                        <div className="tablee-general-config__tags-row tablee-general-config__tags-row--head">
                          <span>Tag</span>
                          <span>Acciones</span>
                        </div>
                        {normalizeAboutTags(draft).map((tag, index) => {
                          const isEditing = editingAboutTagIndex === index;
                          return (
                            <div className="tablee-general-config__tags-row" key={`${tag}-${index}`}>
                              <input
                                type="text"
                                value={tag}
                                readOnly={!isEditing}
                                className={isEditing ? "is-editing" : ""}
                                onFocus={() => setEditingAboutTagIndex(index)}
                                onClick={() => setEditingAboutTagIndex(index)}
                                onChange={(event) => updateAboutTag(index, event.target.value)}
                                onBlur={() => setEditingAboutTagIndex(-1)}
                              />
                              <div className="tablee-general-config__tag-actions">
                                <button
                                  type="button"
                                  className="tablee-carousel__icon-button"
                                  onClick={() => setEditingAboutTagIndex(isEditing ? -1 : index)}
                                  aria-label={isEditing ? `Terminar edición de ${tag}` : `Editar ${tag}`}
                                >
                                  <span className="material-symbols-outlined">{isEditing ? "check" : "edit"}</span>
                                </button>
                                <button
                                  type="button"
                                  className="tablee-carousel__danger"
                                  onClick={() => deleteAboutTag(index)}
                                  aria-label={`Eliminar ${tag}`}
                                >
                                  <span className="material-symbols-outlined">delete</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        {normalizeAboutTags(draft).length === 0 ? (
                          <p>No hay tags agregados.</p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </AnimatedTabPanel>
              </>
            ) : activeSection.id === "delivery-notice" ? (
              <div className="tablee-general-config__delivery-notice">
                <div className="tablee-general-config__delivery-notice-toggle">
                  <div>
                    <strong>Mostrar debajo del menú del sitio</strong>
                    <small>El aviso aparece en todas las páginas y abre el detalle con un clic.</small>
                  </div>
                  <label className="tablee-carousel__switch">
                    <input
                      type="checkbox"
                      checked={draft.deliveryNoticeEnabled !== false}
                      onChange={(event) => updateDraft("deliveryNoticeEnabled", event.target.checked)}
                    />
                    <span />
                    {draft.deliveryNoticeEnabled !== false ? "Visible" : "Oculto"}
                  </label>
                </div>
                <div className="tablee-carousel__form-grid">
                  {activeSection.fields.map(([field, label, type]) => (
                    <label className={type === "textarea" ? "tablee-carousel__field tablee-carousel__wide-field" : "tablee-carousel__field"} key={field}>
                      <span>{label}</span>
                      {type === "textarea" ? (
                        <textarea value={draft[field] || ""} onChange={(event) => updateDraft(field, event.target.value)} />
                      ) : (
                        <input type="text" value={draft[field] || ""} onChange={(event) => updateDraft(field, event.target.value)} />
                      )}
                    </label>
                  ))}
                </div>
                <div className="tablee-general-config__delivery-preview" aria-label="Vista previa del aviso">
                  <span className="material-symbols-outlined">local_shipping</span>
                  <strong>{draft.deliveryNoticeText || "Mensaje de despacho"}</strong>
                  <span>{draft.deliveryNoticeLinkLabel || "Ver condiciones"}</span>
                </div>
              </div>
            ) : (
              <div className="tablee-carousel__form-grid">
                {activeSection.fields.map(([field, label, type]) => (
                  <label className={type === "textarea" ? "tablee-carousel__field tablee-carousel__wide-field" : "tablee-carousel__field"} key={field}>
                    <span>{label}</span>
                    {type === "textarea" ? (
                      <textarea value={draft[field] || ""} onChange={(event) => updateDraft(field, event.target.value)} />
                    ) : type === "number" ? (
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={draft[field] ?? ""}
                        onChange={(event) => updateDraft(field, event.target.value)}
                      />
                    ) : (
                      <input type={type || "text"} value={draft[field] || ""} onChange={(event) => updateDraft(field, event.target.value)} />
                    )}
                  </label>
                ))}
              </div>
            )}

            <div className="tablee-carousel__modal-actions">
              <div className="tablee-carousel__footer-left">
                {activeSection.id === "about" && aboutModalTab === "image" ? (
                  <span className="tablee-home-admin__file-picker tablee-general-config__about-footer-upload">
                    <input ref={aboutImageInputRef} type="file" accept="image/*" onChange={handleAboutImageFile} />
                    <span className="tablee-home-admin__file-button">
                      <span className="material-symbols-outlined">upload</span>
                      {isUploadingAboutImage ? "Subiendo..." : "Agregar imagen"}
                    </span>
                  </span>
                ) : message ? (
                  <p>{message}</p>
                ) : null}
              </div>
              <div className="tablee-carousel__footer-buttons">
                <button type="button" className="tablee-carousel__secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit">Guardar</button>
              </div>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default TableeGeneralConfig;
